'use client'

import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as GridLayout from '@/components/GridLayout/GridLayout'
import { InsuranceDocumentLink } from '@/components/InsuranceDocumentLink'
import { useProductData } from '@/components/ProductData/ProductDataProvider'
import { type InsuranceDocumentFragment, InsuranceDocumentType } from '@/services/graphql/generated'
import { useTracking } from '@/services/Tracking/useTracking'
import { Accordion } from 'ui/src/components/Accordion/Accordion'
import { Heading } from 'ui/src/components/Heading/Heading'
import { Text } from 'ui/src/components/Text/Text'
import { descriptionSection, documentLink, documentsList, wrapper } from './ProductDocuments.css'

type DocumentsByType = Map<InsuranceDocumentType, Array<InsuranceDocumentFragment & { typeOfContract: string }>>

type ProductDocumentsProps = {
  heading: string
  description: string
}

export function ProductDocuments({ heading, description }: ProductDocumentsProps) {
  const tracking = useTracking()
  const productData = useProductData()
  const getDocumentTypeTitle = useGetDocumentTypeTitle()
  const [openedItem, setOpenedItem] = useState<InsuranceDocumentType>()

  const docs = useMemo(() => {
    const documentsByType: DocumentsByType = new Map()
    const allDocuments = productData.variants.flatMap((variant) =>
      variant.documents.map((doc) => ({
        ...doc,
        typeOfContract: variant.typeOfContract,
      })),
    )
    allDocuments.forEach((doc) => {
      if (!documentsByType.has(doc.type)) {
        documentsByType.set(doc.type, [])
      }
      documentsByType.get(doc.type)?.push(doc)
    })

    return documentsByType
  }, [productData.variants])

  const isEmpty = Array.from(docs.values()).every((docList) => docList.length === 0)
  if (isEmpty) {
    console.warn('No documents to show; hiding productDocuments')
    return null
  }

  return (
    <GridLayout.Root className={wrapper}>
      <GridLayout.Content width="1/2" align="left">
        <section className={descriptionSection}>
          <Heading as="h2" variant={{ _: 'standard.24', lg: 'standard.32' }}>
            {heading}
          </Heading>
          <Text size={{ _: 'xl', lg: 'xxl' }} color="textSecondary">
            {description}
          </Text>
        </section>
      </GridLayout.Content>
      <GridLayout.Content width="1/2" align="right">
        <Accordion.Root
          type="single"
          collapsible={true}
          onValueChange={(value: InsuranceDocumentType) => setOpenedItem(value)}
        >
          {Array.from(docs.entries()).map(([type, docList]) => (
            <Accordion.Item key={type} value={type}>
              <Accordion.HeaderWithTrigger>
                <Text size={{ _: 'md', lg: 'lg' }}>{getDocumentTypeTitle(type)}</Text>
              </Accordion.HeaderWithTrigger>

              <Accordion.Content open={openedItem === type} fullWidth={true}>
                <ul className={documentsList}>
                  {docList.map((doc, index) => (
                    <li key={index}>
                      <InsuranceDocumentLink
                        className={documentLink}
                        url={doc.url}
                        displayName={doc.displayName}
                        onClick={() => {
                          if (doc.type === InsuranceDocumentType.TermsAndConditions) {
                            tracking.reportClickTermsAndConditions(productData.name, doc.typeOfContract)
                          }
                        }}
                      />
                    </li>
                  ))}
                </ul>
              </Accordion.Content>
            </Accordion.Item>
          ))}
        </Accordion.Root>
      </GridLayout.Content>
    </GridLayout.Root>
  )
}

function useGetDocumentTypeTitle() {
  const { t } = useTranslation('common')

  return useCallback(
    (type: InsuranceDocumentType) => {
      switch (type) {
        case InsuranceDocumentType.GeneralTerms:
          return t('GENERAL_TERMS_DOCUMENT_TYPE_GROUP')
        case InsuranceDocumentType.PreSaleInfo:
          return t('PRE_SALE_INFO_DOCUMENT_TYPE_GROUP')
        case InsuranceDocumentType.PreSaleInfoEuStandard:
          return t('PRE_SALE_INFO_STANDARD_DOCUMENT_TYPE_GROUP')
        case InsuranceDocumentType.PrivacyPolicy:
          return t('PRIVACY_POLICY_DOCUMENT_TYPE_GROUP')
        case InsuranceDocumentType.ScarTable:
          return t('SCAR_TABLE_DOCUMENT_TYPE_GROUP')
        case InsuranceDocumentType.TermsAndConditions:
          return t('TERMS_AND_CONDITIONS_DOCUMENT_TYPE_GROUP')
        default:
          return type
      }
    },
    [t],
  )
}
